import React, { useState, useRef } from "react";
import "./modal_add_edit_for_wo.css";
import { Button, Form, Input, Radio, Modal, Table, Space, Select, InputNumber, message } from "antd";
import type { FormInstance, InputRef, TableColumnsType, TableColumnType } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import useSWR from "swr";
import { useAuth } from "../../context/AuthContext";


interface ItemDataType {
    key: string;
    code: string;
    description: string;
    available_qty: number;
    price: number;
    uom: string;
    price_flate_rate?: string;
    source_type?: 'service' | 'sparepart';
}

interface Warehouse {
    value: string;
    label: string;
}

interface ModalAddEditForWoProps {
    form: FormInstance;
    onFinish: (values: any) => void;
    onCancel: () => void;
    isEditing: boolean;
    existingItemCodes?: string[];
}

type DataIndex = keyof ItemDataType;

const fetcher = (url: string) => fetch(url).then((res) => res.json());

const ModalAddEditForWo: React.FC<ModalAddEditForWoProps> = ({ form, onFinish, onCancel, isEditing, existingItemCodes }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const qtyInputRef = useRef<HTMLInputElement>(null);
    const [flatRate, setFlatRate] = useState<number>(0);
    const [selectedJobType, setSelectedJobType] = useState<string | null>(null);
    const [selectedType, setSelectedType] = useState<string | null>(form.getFieldValue('type'));
    const [selectedTrxType, setSelectedTrxType] = useState<string | null>(form.getFieldValue('trx_type'));
    const [selectedWarehouse, setSelectedWarehouse] = useState<string | null>(null);
    const watchedType = Form.useWatch('type', form);
    const watchedTrxType = Form.useWatch('trx_type', form);
    const watchedJobType = Form.useWatch('job_type', form);
    const watchedWarehouse = Form.useWatch('warehouse', form);
    const [proposalDiscType, setProposalDiscType] = useState('percent');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const { userData } = useAuth();
    const { data: warehouses, error: warehousesError } = useSWR<Warehouse[]>(
        userData?.kode_grup1 ? `${process.env.REACT_APP_BACKEND_URL}/api/work-order/warehouses?kode_grup1=${userData.kode_grup1}` : null,
        fetcher
    );

    React.useEffect(() => {
        if (!isEditing && userData?.warehouse_code) {
            form.setFieldsValue({ warehouse: userData.warehouse_code });
            setSelectedWarehouse(userData.warehouse_code);
        }
    }, [userData, form, isEditing]);

    React.useEffect(() => {
        setSelectedType(watchedType || null);
    }, [watchedType]);
    React.useEffect(() => {
        setSelectedTrxType(watchedTrxType || null);
    }, [watchedTrxType]);
    React.useEffect(() => {
        setSelectedJobType(watchedJobType || null);
    }, [watchedJobType]);
    React.useEffect(() => {
        setSelectedWarehouse(watchedWarehouse || null);
    }, [watchedWarehouse]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortField, setSortField] = useState<string | undefined>(undefined);
    const [sortOrder, setSortOrder] = useState<string | undefined>(undefined);
    const { data: modalItemsData, error: modalItemsError } = useSWR<{ data: ItemDataType[], total: number }>(
        selectedWarehouse && isModalOpen
            ? `${process.env.REACT_APP_BACKEND_URL}/api/work-order/modal-items?warehouse_group=${selectedWarehouse}&type=${selectedType || ''}&page=${currentPage}&limit=${pageSize}&search=${searchText}&sortField=${sortField || ''}&sortOrder=${sortOrder || ''}`
            : null,
        fetcher
    );
    React.useEffect(() => {
        setCurrentPage(1);
    }, [selectedType, selectedWarehouse]);

    const warehouseOptions = warehouses?.map((warehouse) => ({
        label: warehouse.label,
        value: warehouse.value,
    })) || [];

    const handleProposalDiscChange = (e: any) => {
        setProposalDiscType(e.target.value);
    };

    const showModal = () => {
        setIsModalOpen(true);
    };

    const handleCancelModal = () => {
        setIsModalOpen(false);
        setSearchText('');
        setSearchedColumn('');
        setSortField(undefined);
        setSortOrder(undefined);
        setCurrentPage(1);
    };

    const handleSelectItem = (record: ItemDataType) => {
        if (existingItemCodes?.includes(record.code)) {
            message.error("Item Code sudah terdaftar di tabel Spareparts Detail!");
            return;
        }
        const rate = parseFloat(record.price_flate_rate || "0");
        setFlatRate(rate);
        const currentType = form.getFieldValue('type');
        const resolvedPrice = (currentType === '4' && record.source_type === 'service') ? rate : record.price;
        const fieldsToSet: any = {
            item_code: record.code,
            item_name: record.description,
            uom: record.source_type === 'service' ? '' : record.uom,
            supply_qty: record.source_type === 'service' ? 0 : record.available_qty,
            price: resolvedPrice,
        };
        let qty = 0;
        if (record.source_type === 'service') {
            fieldsToSet.qty = record.available_qty;
            qty = record.available_qty;
        }

        let subTotal = 0;
        if (currentType === '4') {
            if (record.source_type === 'service') {
                subTotal = resolvedPrice * qty;
            } else {
                subTotal = resolvedPrice;
            }
        } else {
            subTotal = resolvedPrice * qty;
        }


        fieldsToSet.sub_total = subTotal;
        fieldsToSet.total = subTotal;

        form.setFieldsValue(fieldsToSet);
        handleCancelModal();
    };

    const handleFormValuesChange = (changedValues: any, allValues: any) => {

        if ((changedValues.disc_percent !== undefined || changedValues.disc_amount !== undefined) && (allValues.type === '1' || allValues.type === '4')) {

            if (!allValues.qty || allValues.qty <= 0) {
                message.warning("Mohon isi Qty terlebih dahulu sebelum mengisi Diskon.");
                form.setFieldsValue({
                    disc_percent: null,
                    disc_amount: null
                });

                qtyInputRef.current?.focus();
                return;
            }
        }

        if (changedValues.type) {
            setSelectedType(changedValues.type);
        }
        if (changedValues.trx_type) {
            setSelectedTrxType(changedValues.trx_type);
        }
        if (changedValues.job_type) {
            setSelectedJobType(changedValues.job_type);
        }
        if (changedValues.warehouse) {
            setSelectedWarehouse(changedValues.warehouse);
        }

        if ((changedValues.type === '4' && allValues.trx_type === '2') || (changedValues.trx_type === '2' && allValues.type === '4')) {
            Modal.warning({
                title: 'Perhatian !',
                content: 'Terpilih Trx External berarti pengerjaan di lakukan dengan jasa external',
            });
        }

        if (changedValues.type || changedValues.trx_type || changedValues.job_type || changedValues.warehouse) {
            const isExtSparepart = allValues.type === "1" && allValues.trx_type === "2";
            const isExtOperation = allValues.type === "4" && allValues.trx_type === "2";
            form.setFieldsValue({
                item_code: isExtSparepart ? "External Part" : (isExtOperation ? "External Operation" : null),
                item_name: null,
                uom: null,
                qty: null,
                supply_qty: null,
                price: null,
                sub_total: null,
                disc_percent: null,
                disc_amount: null,
                total: null
            });
            return;
        }

        if (
            changedValues.price !== undefined ||
            changedValues.qty !== undefined ||
            changedValues.disc_amount !== undefined ||
            changedValues.disc_percent !== undefined ||
            changedValues.type !== undefined ||
            changedValues.trx_type !== undefined
        ) {

            let price = allValues.price || 0;
            const qty = allValues.qty || 0;
            if (allValues.trx_type === "2" && allValues.type === "4" && allValues.item_code !== "External Operation") {
                price = qty * flatRate;
                form.setFieldsValue({ price: price });
            }
            let subTotal;
            if (allValues.type === '4') {
                subTotal = price * qty;
            } else {
                subTotal = price * qty;
            }
            let discAmount = parseFloat(allValues.disc_amount) || 0;
            const fieldsToUpdate: { [key: string]: any } = {
                sub_total: subTotal,
            };

            if (allValues.type === "1" && allValues.trx_type === "2") {
                fieldsToUpdate.supply_qty = qty;
            }

            if (changedValues.disc_percent !== undefined) {
                const discPercent = parseFloat(allValues.disc_percent) || 0;
                let calculatedDiscAmount = 0;

                if (allValues.type === '1' || allValues.type === '4') {
                    calculatedDiscAmount = (price * discPercent) / 100;
                } else {
                }

                discAmount = calculatedDiscAmount;
                fieldsToUpdate.disc_amount = calculatedDiscAmount;
            } else if (changedValues.disc_amount !== undefined) {
                if (price > 0) {
                    const calculatedDiscPercent = (discAmount / price) * 100;
                    fieldsToUpdate.disc_percent = calculatedDiscPercent.toFixed(2);
                } else {
                    fieldsToUpdate.disc_percent = 0;
                }
            }

            let total = 0;
            if (allValues.type === '1' || allValues.type === '4') {
                total = (price * qty) - (discAmount * qty);
            } else {
                total = subTotal - discAmount;
            }
            fieldsToUpdate.total = total;

            form.setFieldsValue(fieldsToUpdate);
        }
    };

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0] || '');
        setSearchedColumn(dataIndex);
        setCurrentPage(1);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn('');
        setCurrentPage(1);
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<ItemDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) => {
            const val = record[dataIndex];
            return val != null
                ? val.toString().toLowerCase().includes((value as string).toLowerCase())
                : false;
        },
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<ItemDataType> = [
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            sorter: true,
            ...getColumnSearchProps('code'),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            sorter: true,
            ...getColumnSearchProps('description'),
        },
        {
            title: selectedType === "4" ? "Frt" : (selectedType === "1" ? "Qty" : "Qty / FRT"),
            dataIndex: 'available_qty',
            key: 'available_qty',
            sorter: true,
            ...getColumnSearchProps('available_qty'),
            render: (value, record) => {
                if (record.source_type === 'service') {
                    return value;
                }
                return value;
            }
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            sorter: true,
            ...getColumnSearchProps('price'),
            render: (text: string | number) => {
                const numericValue = typeof text === 'string' ? parseFloat(text) : text;
                if (isNaN(numericValue)) {
                    return text;
                }
                return numericValue.toLocaleString('id-ID');
            },
        },
    ];

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
        if (sorter && sorter.field) {
            setSortField(sorter.field);
            setSortOrder(sorter.order);
        } else {
            setSortField(undefined);
            setSortOrder(undefined);
        }
    };

    const isExternalSparepart = selectedType === "1" && selectedTrxType === "2";
    const isExternalOperation = selectedType === "4" && selectedTrxType === "2";
    const showHpp = isExternalSparepart || isExternalOperation;


    return (
        <>
            <div id="i1ij" style={transformStyle}>
                <div className="gjs-grid-row" id="ige6">
                    <div className="gjs-grid-column" id="i8eq">
                        <Form
                            form={form}
                            onValuesChange={handleFormValuesChange}
                            layout="horizontal"
                            style={{ maxWidth: 600 }}
                            labelCol={{ style: { width: '200px', textAlign: "left" } }}
                            wrapperCol={{ style: { width: '290px' } }}
                            className="custom-info-purchase-request"
                        >
                            <Form.Item
                                label="Type"
                                name="type"
                                style={{
                                    marginBottom: "5px",
                                }}
                                rules={[{ required: true, message: 'Required !' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder=""
                                    optionFilterProp="label"
                                    style={{ width: "100%" }}
                                    getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                                    options={[
                                        { label: "— Empty —-", value: "" },
                                        { label: "Sparepart", value: "1" },
                                        { label: "Operation", value: "4" },
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Trx Type"
                                name="trx_type"
                                style={{
                                    marginBottom: "5px",
                                }}
                                rules={[{ required: true, message: 'Required !' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder=""
                                    optionFilterProp="label"
                                    style={{ width: "100%" }}
                                    getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                                    options={[
                                        { label: "— Empty —-", value: "" },
                                        { label: "Internal", value: "1" },
                                        { label: "External", value: "2" },
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Job Type"
                                name="job_type"
                                style={{
                                    marginBottom: "5px",
                                }}
                                rules={[{ required: true, message: 'Required !' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder=""
                                    optionFilterProp="label"
                                    style={{ width: "100%" }}
                                    getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                                    options={[
                                        { label: "General Repair", value: "GR" },
                                        { label: "Periodical Maintenance", value: "PM" },
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Warehouse"
                                name="warehouse"
                                style={{
                                    marginBottom: "5px",
                                }}
                                rules={[{ required: true, message: 'Required !' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Select a warehouse"
                                    optionFilterProp="label"
                                    style={{ width: "100%" }}
                                    getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                                    options={warehouseOptions}
                                    loading={!warehouses && !warehousesError}
                                    disabled={!warehouses && !warehousesError}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Item / Operation Code"
                                style={{ marginBottom: "5px" }} >
                                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                    <Form.Item
                                        name="item_code"
                                        noStyle
                                        style={{ width: "100%" }}>
                                        <Input
                                            style={{ width: "100%" }}
                                            readOnly
                                        />
                                    </Form.Item>
                                    <Button
                                        type="primary"
                                        id="list_item_service"
                                        onClick={showModal}
                                        disabled={isExternalSparepart || isExternalOperation}
                                        icon={
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" style={{ width: "40px", height: "20px" }}>
                                                <path d="M11.625 16.5a1.875 1.875 0 1 0 0-3.75 1.875 1.875 0 0 0 0 3.75Z" />
                                                <path fillRule="evenodd" d="M5.625 1.5H9a3.75 3.75 0 0 1 3.75 3.75v1.875c0 1.036.84 1.875 1.875 1.875H16.5a3.75 3.75 0 0 1 3.75 3.75v7.875c0 1.035-.84 1.875-1.875 1.875H5.625a1.875 1.875 0 0 1-1.875-1.875V3.375c0-1.036.84-1.875 1.875-1.875Zm6 16.5c.66 0 1.277-.19 1.797-.518l1.048 1.048a.75.75 0 0 0 1.06-1.06l-1.047-1.048A3.375 3.375 0 1 0 11.625 18Z" clipRule="evenodd" />
                                                <path d="M14.25 5.25a5.23 5.23 0 0 0-1.279-3.434 9.768 9.768 0 0 1 6.963 6.963A5.23 5.23 0 0 0 16.5 7.5h-1.875a.375.375 0 0 1-.375-.375V5.25Z" />
                                            </svg>}
                                    />
                                    <Form.Item
                                        name="item_name"
                                        noStyle
                                        style={{ width: "100%" }}
                                        rules={(isExternalSparepart || isExternalOperation) ? [{ required: true, message: 'Wajib diisi!' }] : []}
                                    >
                                        <Input
                                            style={{ width: "100%" }}
                                            readOnly={!(isExternalSparepart || isExternalOperation)}
                                        />
                                    </Form.Item>
                                </div>
                            </Form.Item>
                            <Form.Item
                                label="UoM"
                                name="uom"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <Input readOnly />
                            </Form.Item>
                            <Form.Item
                                label={selectedType === "4" ? "FRT" : (selectedType === "1" ? "QTY" : "FRT / QTY")}
                                name="qty"
                                style={{
                                    marginBottom: "5px",
                                    maxWidth: "50%"
                                }}
                                rules={isExternalSparepart ? [{ required: true, message: 'Wajib diisi!' }] : [{ required: true, message: 'Required !' }]}
                            >
                                <InputNumber ref={qtyInputRef} min={0} style={{ width: "100%" }} placeholder="" />
                            </Form.Item>
                            <Form.Item
                                label="Stock Ready"
                                name="supply_qty"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <Input readOnly />
                            </Form.Item>
                            <Form.Item
                                label="HPP"
                                name="hpp"
                                style={{
                                    marginBottom: "5px",
                                }}
                                rules={showHpp ? [{ required: true, message: 'Wajib diisi!' }] : []}
                                hidden={!showHpp}
                            >
                                <InputNumber
                                    style={{ width: "100%" }}
                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                                    parser={(value) => value!.replace(/\./g, '')}
                                    placeholder=""
                                    readOnly={!showHpp}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Price"
                                name="price"
                                style={{
                                    marginBottom: "5px",
                                }}
                                rules={(isExternalSparepart || isExternalOperation) ? [{ required: true, message: 'Wajib diisi!' }] : []}
                            >
                                <InputNumber
                                    style={{ width: "100%" }}
                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                                    parser={(value) => value!.replace(/\./g, '')}
                                    placeholder=""
                                    readOnly={!(isExternalSparepart || isExternalOperation)}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Sub Total"
                                name="sub_total"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <InputNumber
                                    style={{ width: "100%" }}
                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                                    parser={(value) => value!.replace(/\./g, '')}
                                    placeholder=""
                                    readOnly
                                />
                            </Form.Item>
                            <Form.Item
                                label="Proposal Disc"
                                name="proposal_disc_type"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <Radio.Group onChange={handleProposalDiscChange} defaultValue="percent">
                                    <Radio value="percent">%</Radio>
                                    <Radio value="amount">Amount</Radio>
                                </Radio.Group>
                            </Form.Item>
                            <Form.Item
                                label="Disc Percent"
                                name="disc_percent"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <Input placeholder="" disabled={proposalDiscType === 'amount'} />
                            </Form.Item>
                            <Form.Item
                                label="Disc Amount"
                                name="disc_amount"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <InputNumber
                                    style={{ width: "100%" }}
                                    placeholder=""
                                    disabled={proposalDiscType === 'percent'}
                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                                    parser={(value) => value!.replace(/\./g, '')}
                                />
                            </Form.Item>
                            <Form.Item
                                label="Total"
                                name="total"
                                style={{
                                    marginBottom: "5px",
                                }}
                            >
                                <InputNumber
                                    style={{ width: "100%" }}
                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                                    parser={(value) => value!.replace(/\./g, '')}
                                    placeholder=""
                                    readOnly
                                />
                            </Form.Item>
                        </Form>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
                            <Button onClick={onCancel}>Cancel</Button>
                            <Button type="primary" onClick={() => {
                                form.validateFields()
                                    .then(values => {
                                        const { supply_qty, ...rest } = values;
                                        onFinish(rest);
                                        const currentType = form.getFieldValue('type');
                                        const currentTrxType = form.getFieldValue('trx_type');
                                        const currentJobType = form.getFieldValue('job_type');
                                        const currentWarehouse = form.getFieldValue('warehouse');
                                        const currentItemCode = form.getFieldValue('item_code');
                                        form.resetFields();
                                        form.setFieldsValue({
                                            type: currentType,
                                            trx_type: currentTrxType,
                                            job_type: currentJobType,
                                            warehouse: currentWarehouse
                                        });
                                        if (currentTrxType === '2') {
                                            form.setFieldsValue({ item_code: currentItemCode });
                                        }
                                        setFlatRate(0);
                                        setProposalDiscType('percent');
                                    })
                                    .catch(info => {
                                        if (info.errorFields.some((f: any) => f.name.includes('qty'))) {
                                            qtyInputRef.current?.focus();
                                        }
                                    });
                            }}>{isEditing ? 'Edit' : 'Ok'}</Button>
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                title="List Item / Service"
                className="list-item-service"
                open={isModalOpen}
                onCancel={handleCancelModal}
                width={1000}
                destroyOnClose={true}
                footer={[
                    <Button key="back" onClick={handleCancelModal}>
                        Close
                    </Button>,
                ]}
            >
                <Table<ItemDataType>
                    columns={columns}
                    dataSource={modalItemsData?.data || []}
                    loading={!modalItemsData && !modalItemsError && !!selectedWarehouse}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: modalItemsData?.total || 0,
                        showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                    onRow={(record) => ({
                        onDoubleClick: () => {
                            handleSelectItem(record);
                        },
                    })}
                />
            </Modal>
        </>
    );
};

export default ModalAddEditForWo;